import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utilities/axiosInstance";
import { filterRequest } from "../../utilities/apiManager";

const initialState = {
  isLoading: false,
  data: [],
  itemsCount: 0,
  pages: 0,
  error: null,
  lastParams: null, // keep last fetch args to refetch after C/U/D
};

export const getFaq = createAsyncThunk("faq/getFaq", async (args, thunkApi) => {
  try {
    const { page, size = 10, query = "", userType } = args;
    const filter = filterRequest(args.filter);
    const { data } = await axiosInstance.get(
      `/faqs?userType=${userType}&page=${page}&size=${size}&query=${encodeURIComponent(
        query
      )}${filter}`
    );
    return thunkApi.fulfillWithValue({ payload: data, args });
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors?.response?.data ?? { message: "Unknown error" }
    );
  }
});

// store/settings/faqSlice.js

export const createFaq = createAsyncThunk(
  "faq/createFaq",
  async (args, thunkApi) => {
    try {
      // args should have ONLY: userType, titleAr, titleEn, descriptionAr, descriptionEn
      const payload = {
        userType: args.userType,
        titleAr: args.titleAr,
        titleEn: args.titleEn,
        descriptionAr: args.descriptionAr,
        descriptionEn: args.descriptionEn,
      };

      // FIX: plural endpoint
      const { data } = await axiosInstance.post(`/faqs`, payload);
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      return thunkApi.rejectWithValue(
        errors?.response?.data ?? { message: "Unknown error" }
      );
    }
  }
);

export const updateFaq = createAsyncThunk(
  "faq/updateFaq",
  async (args, thunkApi) => {
    try {
      // FIX: whitelist fields so _id, __v never go in the body
      const v = args.values || {};
      const payload = {
        userType: v.userType,
        titleAr: v.titleAr,
        titleEn: v.titleEn,
        descriptionAr: v.descriptionAr,
        descriptionEn: v.descriptionEn,
      };

      const { data } = await axiosInstance.put(`/faqs/${args._id}`, payload);
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      return thunkApi.rejectWithValue(
        errors?.response?.data ?? { message: "Unknown error" }
      );
    }
  }
);

export const deleteFaq = createAsyncThunk(
  "faq/deleteFaq",
  async (args, thunkApi) => {
    try {
      const { data } = await axiosInstance.delete(`/faqs/${args._id}`);
      return thunkApi.fulfillWithValue(data);
    } catch (errors) {
      return thunkApi.rejectWithValue(
        errors?.response?.data ?? { message: "Unknown error" }
      );
    }
  }
);

const faqSlice = createSlice({
  name: "faq",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getFaq.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.lastParams = action.meta.arg ?? state.lastParams;
      })
      .addCase(getFaq.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.error = null;
        state.data = payload.payload?.data?.data ?? [];
        state.itemsCount = payload.payload?.data?.itemsCount ?? 0;
        state.pages = payload.payload?.data?.pages ?? 0;
      })
      .addCase(getFaq.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message ?? "Failed loading FAQs";
      })

      // CREATE
      .addCase(createFaq.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFaq.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(createFaq.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message ?? "Failed creating FAQ";
      })

      // UPDATE
      .addCase(updateFaq.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFaq.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateFaq.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message ?? "Failed updating FAQ";
      })

      // DELETE
      .addCase(deleteFaq.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFaq.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(deleteFaq.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload?.message ?? "Failed deleting FAQ";
      });
  },
});

export default faqSlice.reducer;
